<template lang="pug">
#app.flex.flex-col.h-full.bg-no-repeat.bg-cover.bg-center(:style="appLobbyUrl ? `--appLobbyBg :url(${appLobbyUrl})` : null")
  .flex.w-full.h-full(v-if="!isInGame")
    //- GuestSide（預留空間，資料取得後顯示）
    .main-side(:class="{ invisible: !menu }")
      GuestSide(v-if="menu", :menuList="menuList", :menuItemStack="menuItemStack", :historyStack="historyStack", @clickLogo="enterDefaultRoom")
    .flex.flex-1.flex-col.w-0
      GuestHeader.mr-2(:menuItemStack="menuItemStack", :menuList="menuList", @clickLogo="enterDefaultRoom")
      transition(name="slide-up", mode="out-in")
        GuestMain.flex-1.min-h-0.pb-6(v-if="!isInGame", class="landscape:xl:pt-4 portrait:pt-2", @clickLogo="enterDefaultRoom")
  transition(name="slide-up", mode="out-in")
    GuestFooter(v-if="!isInGame")
  transition(name="slide-left", mode="out-in", @before-enter="isBroadcastMoveable = false", @after-enter="isBroadcastMoveable = true", @before-leave="isBroadcastMoveable = false", @after-leave="isBroadcastMoveable = false")
    LazyBroadcast.broadcast(v-if="normalBroadcast && !isInGame", :broadcast="normalBroadcast", :key="normalBroadcast.broadcastId", :isBroadcastMoveable="isBroadcastMoveable")
  NuxtPage
  #portal-main
  PwaInstruction
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'

import { useMainStore } from '@/stores/main'
import { storage } from '@/utils/storage'

const route = useRoute()

// 初始化 navbar（觸發 useFetch 取得 navbar 配置，數據存入 navbarStore）
useNavbar()

const { announcementList, fetchAnnouncements } = useCommon()
const { getBackCdnUrl } = useCdnAssets()

const { menu, menuItemStack, historyStack, defaultMenuItemStack, getMenu, stopAutoUpdate, reset } = useSideMenu()

const { stopAutoUpdate: gameStopAutoUpdate, setRoom: gameSetRoom } = useGame()

const { jackpotList, setJackpotEnable, stopAutoRefresh: jackpotStopAutoRefresh } = useJackpot()

const { $dayjs, $SoundControl, $tgWebAppShowBtn, $tgWebAppSetBtnEvt, $tgWebAppHideBtn } = useNuxtApp()

const mainStore = useMainStore()
const { getGameSearchPageEnable, getNormalBroadcast } = storeToRefs(mainStore)
const { setJackpotList, setTrackLocation, setGameSearchPageEnable, setSystemModal, setSystemModalParams, setModal } = mainStore

const isBroadcastMoveable = ref(false)
const appLobbyUrl = getBackCdnUrl('icon/pwa_lobby.png')
const announcementsFetched = ref(false)

const isInGame = computed(() => {
  return route.path === '/guest/game' || route.path === '/guest/game/'
})

const normalBroadcast = computed(() => getNormalBroadcast.value)

const menuList = computed(() => {
  return menu.value?.data || []
})

// SSR 優化：只在客戶端執行，避免 SSR 期間建立閉包導致記憶體洩漏
if (import.meta.client) {
  watch([announcementList, announcementsFetched], ([list, fetched]) => {
    if (!fetched || !list?.length) return
    const today = $dayjs().hour(0).minute(0).second(0).millisecond(0).format('YYYYMMDD')
    if (!(storage.get(`announcement_${today}_display_off`) === 'true')) {
      const filteredList = list.filter((item) => item.isBroadcast && (item.displayMode === 'ONLY_POPUP_ANNOUNCEMENT' || item.displayMode === 'BOTH'))
      if (filteredList.length > 0) {
        setSystemModalParams({ list: filteredList })
        setSystemModal('ModalAnnouncement')
      }
    }
  })

  watch(jackpotList, (list = []) => {
    const currentTime = Date.now()
    const jackpots = list.map((jackpot) => ({
      ...jackpot,
      jackpotStart: jackpot?.startTime <= currentTime,
    }))
    setJackpotList(jackpots)
  })
}

async function enterDefaultRoom() {
  if (getGameSearchPageEnable.value) {
    setGameSearchPageEnable(false)
  }
  const lastItem = defaultMenuItemStack.value.slice(-1)[0]
  reset()
  gameSetRoom(lastItem)
}

onMounted(async () => {
  getMenu()
  try {
    await fetchAnnouncements()
  } finally {
    announcementsFetched.value = true
  }
  await setJackpotEnable(true)
  setTrackLocation('lobby')
  $SoundControl.background.play('lobby')

  const { initData } = window?.Telegram?.WebApp || {}
  if (initData) {
    $tgWebAppShowBtn('SettingsButton')
    $tgWebAppSetBtnEvt('SettingsButton', () => {
      $SoundControl.sound.play('click')
      setModal('ModalSetting')
    })
  }
})

onBeforeUnmount(() => {
  gameStopAutoUpdate()
  stopAutoUpdate()
  jackpotStopAutoRefresh()

  const { initData } = window?.Telegram?.WebApp || {}
  if (initData) {
    $tgWebAppHideBtn('SettingsButton')
  }
})
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

#app {
  background-image: var(--appLobbyBg);
}

.main-side {
  display: none;
}

@media screen and (min-width: 1024px) and (orientation: landscape) {
  .main-side {
    display: flex;
    width: 200px;
    min-width: 200px;
  }
}
</style>
